import { useState } from 'react';
import { getUserDisplayName } from 'shared-helpers/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getFormattedDate } from 'lib/common';
import { useReduxState } from 'hooks';
import { TimeIndicator, Tooltip, User } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';
import { Review, ReviewAdd, ReviewEditable } from '.';

import type {
  BlockOpenQuestion,
  Event,
  TaskAnswerReview,
  UserWithBasicData
} from 'shared-types';

interface Props {
  answerEvents?: Pick<Event, 'createdAt' | 'id' | 'type'>[];
  answerId: number;
  answerUser: UserWithBasicData;
  courseId: number;
  gradingType: BlockOpenQuestion['gradingType'];
  isAdmin: boolean;
  isTaskEditable?: boolean;
  maxPoints?: number;
  onDataChange?: () => Promise<void>;
  onlyComments: boolean;
  reviews: TaskAnswerReview[];
}

const Reviews = ({
  answerEvents,
  answerId,
  answerUser,
  courseId,
  gradingType,
  isAdmin,
  isTaskEditable,
  maxPoints,
  onDataChange,
  onlyComments = false,
  reviews
}: Props) => {
  const {
    language,
    messages,
    serverConfig,
    theme,
    user: loggedInUser
  } = useReduxState();

  const [order, setOrder] = useState<'newest' | 'oldest'>('oldest');

  const onOrderChange = (value: 'newest' | 'oldest') => {
    setOrder(value);
  };

  const reviewsAndEvents = [
    ...(reviews || []).map((review) => ({ itemType: 'review', ...review })),
    ...(answerEvents || []).map((event) => ({ itemType: 'event', ...event }))
  ] as (
    | ((typeof reviews)[0] & { itemType: 'review' })
    | ((typeof answerEvents)[0] & { itemType: 'event' })
  )[];

  const sortedReviewsAndEvents = reviewsAndEvents.sort((a, b) => {
    if (order === 'newest') {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    } else {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
  });

  return (
    <div className="reviews">
      <div className="header">
        <div className="title">
          {onlyComments ? messages.commonComments : messages.tasksReviews}
          {` (${reviews?.length || 0})`}
        </div>

        {reviews?.length > 1 && (
          <div className="dropdown-container">
            <DropdownSelectable
              align="right"
              category="light"
              defaultValue={'oldest'}
              dropdownWhiteSpace="nowrap"
              onChange={(value) => onOrderChange(value as 'newest' | 'oldest')}
              options={[
                {
                  text: onlyComments
                    ? messages.readerDiscussionCommentsOrderNewest
                    : messages.tasksNewestFirst,
                  value: 'newest'
                },
                {
                  text: onlyComments
                    ? messages.readerDiscussionCommentsOrderOldest
                    : messages.tasksOldestFirst,
                  value: 'oldest'
                }
              ]}
              showSelectedText={true}
              size="small"
            />
          </div>
        )}
      </div>

      {sortedReviewsAndEvents.length ? (
        <div className="reviews-list">
          {sortedReviewsAndEvents.map((item, i) => (
            <div className="review-or-event" key={i}>
              {item.itemType === 'event' ? (
                <div className="answer-event">
                  <User
                    showNameOrUsername={false}
                    size="tiny"
                    user={answerUser}
                  />

                  {getMessageWithPlaceholders({
                    message: messages.tasksUserEditedTheirAnswer,
                    placeholders: {
                      user: getUserDisplayName({
                        messages,
                        serverConfig,
                        user: answerUser
                      })
                    }
                  })}

                  <Tooltip content={getFormattedDate({ date: item.createdAt })}>
                    <TimeIndicator
                      language={language}
                      timeString={item.createdAt as string}
                    />
                  </Tooltip>
                </div>
              ) : item.itemType === 'review' &&
                isAdmin &&
                item.user &&
                loggedInUser &&
                item.user.id === loggedInUser.id ? (
                <ReviewEditable
                  key={`own-review-${i}`}
                  answerId={answerId}
                  answerUserEmail={answerUser?.email}
                  courseId={courseId}
                  gradingType={gradingType}
                  isTaskEditable={isTaskEditable}
                  maxPoints={maxPoints}
                  onDataChange={onDataChange}
                  onlyComments={onlyComments}
                  review={item}
                />
              ) : item.itemType === 'review' ? (
                <Review
                  courseId={courseId}
                  key={i}
                  isAdmin={isAdmin}
                  onDataChange={onDataChange}
                  review={item}
                />
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {isAdmin ? (
        <div className="add">
          <ReviewAdd
            answerId={answerId}
            answerUserEmail={answerUser?.email}
            courseId={courseId}
            gradingType={gradingType}
            isTaskEditable={isTaskEditable}
            maxPoints={maxPoints}
            onDataChange={onDataChange}
            onlyComments={onlyComments}
            keepOpen={!reviews?.length && !onlyComments}
          />
        </div>
      ) : null}

      <style jsx>{`
        .reviews {
          margin-top: 30px;
          max-width: 640px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.4rem;
          font-weight: 700;
        }
        .dropdown-container {
          display: flex;
          justify-content: flex-end;
        }
        .reviews-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .answer-event {
          align-items: center;
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.4rem;
          gap: 5px;
          padding: 10px 15px;
        }
        .add {
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default Reviews;
